/**
 * AISpeedSlider — In-game slider to control AI thinking/move delay.
 *
 * Shows a range input from "Instant" to "Slow" that controls how long
 * the AI waits before making its move. Adjustable in real-time during gameplay.
 * Only visible in single-player mode.
 */

import React from 'react';
import './AISpeedSlider.css';

interface AISpeedSliderProps {
  /** Current delay in milliseconds (0–3000) */
  value: number;
  /** Called when the user changes the slider */
  onChange: (delayMs: number) => void;
  /** Optional: hide the slider */
  hidden?: boolean;
}

const SPEED_LABELS: { max: number; label: string }[] = [
  { max: 200, label: 'Instant' },
  { max: 600, label: 'Fast' },
  { max: 1200, label: 'Normal' },
  { max: 2000, label: 'Slow' },
  { max: 3000, label: 'Very Slow' },
];

function getSpeedLabel(ms: number): string {
  for (const s of SPEED_LABELS) {
    if (ms <= s.max) return s.label;
  }
  return 'Very Slow';
}

export const AISpeedSlider: React.FC<AISpeedSliderProps> = ({ value, onChange, hidden }) => {
  if (hidden) return null;

  const label = getSpeedLabel(value);

  return (
    <div className="ai-speed-slider" role="group" aria-label="AI speed control">
      <div className="ai-speed-slider__header">
        <span className="ai-speed-slider__label">🤖 AI Speed</span>
        <span className="ai-speed-slider__value">{label} ({(value / 1000).toFixed(1)}s)</span>
      </div>
      <input
        type="range"
        className="ai-speed-slider__input"
        min={0}
        max={3000}
        step={100}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label={`AI move delay: ${label}`}
        aria-valuemin={0}
        aria-valuemax={3000}
        aria-valuenow={value}
        aria-valuetext={`${label}, ${(value / 1000).toFixed(1)} seconds`}
      />
      <div className="ai-speed-slider__ticks">
        <span>Instant</span>
        <span>Slow</span>
      </div>
    </div>
  );
};

export default AISpeedSlider;
